"use strict";
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.periodLabel = exports.money = exports.SERIES_DARK = exports.SERIES_LIGHT = void 0;
exports.StackedCostChart = StackedCostChart;
exports.StackedProfitChart = StackedProfitChart;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
/**
 * RF-2 charts, drawn as inline SVG.
 *
 * There is no charting library in this app, and adding one for two charts is not worth the bundle —
 * the data shape here is library-agnostic, so swapping later costs nothing.
 *
 * The palette is the validated eight-slot categorical set (adjacent-pair CVD ΔE 9.1 light / 8.4
 * dark, normal-vision 19.6 / 19.3). Slots are assigned in fixed order and never cycled: a ninth
 * series folds into "Other" instead of inventing a hue. Three light-mode hues sit below 3:1 on
 * white, so the page ships a table view — that is the required relief, not a nicety.
 */
exports.SERIES_LIGHT = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];
exports.SERIES_DARK = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"];
/** Diverging pair for profit — warm/cool poles that read as opposite. */
var POS = "#2a78d6";
var NEG = "#e34948";
var INK_MUTED = "#898781";
var GRID = "#e1e0d9";
var AXIS = "#c3c2b7";
var money = function (n) {
    return "R".concat(n.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 }));
};
exports.money = money;
var moneyExact = function (n) { return "R".concat(n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })); };
/**
 * Compact axis money — "R1.5m", "R500k".
 *
 * Full figures ("R2 000 000") are wide enough to run off the left of the plot, and an axis only has
 * to convey magnitude; the exact number belongs in the tooltip and the table.
 */
var moneyAxis = function (n) {
    var abs = Math.abs(n);
    var sign = n < 0 ? "-" : "";
    if (abs >= 1000000)
        return "".concat(sign, "R").concat((abs / 1000000).toFixed(abs >= 10000000 ? 0 : 1), "m");
    if (abs >= 1000)
        return "".concat(sign, "R").concat(Math.round(abs / 1000), "k");
    return "".concat(sign, "R").concat(abs.toFixed(0));
};
/** "2026-03" → "Mar 26"; "2026-Q1" → "Q1 26". */
var periodLabel = function (p) {
    var q = p.match(/^(\d{4})-Q(\d)$/);
    if (q)
        return "Q".concat(q[2], " ").concat(q[1].slice(2));
    var m = p.match(/^(\d{4})-(\d{2})$/);
    if (!m)
        return p;
    var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return "".concat(months[Number(m[2]) - 1], " ").concat(m[1].slice(2));
};
exports.periodLabel = periodLabel;
/** Round a maximum up to a readable axis top. */
function niceCeil(value) {
    if (value <= 0)
        return 1;
    var magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    var normalised = value / magnitude;
    var step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
    return step * magnitude;
}
/**
 * Drop ticks that would render on top of each other.
 *
 * A lopsided range — a huge positive top against a tiny negative floor — puts several candidate
 * ticks within a pixel or two of the same y, and their labels then overprint. Deduping by VALUE does
 * not help; the collision is in rendered position, so that is what gets checked.
 */
function spacedTicks(values, yOf, minGapPx) {
    if (minGapPx === void 0) { minGapPx = 16; }
    var sorted = Array.from(new Set(values)).sort(function (a, b) { return b - a; });
    var kept = [];
    var _loop_1 = function (v) {
        if (kept.every(function (k) { return Math.abs(yOf(k) - yOf(v)) >= minGapPx; }))
            kept.push(v);
    };
    for (var _i = 0, sorted_1 = sorted; _i < sorted_1.length; _i++) {
        var v = sorted_1[_i];
        _loop_1(v);
    }
    // Zero always survives — it is the baseline the reader anchors on.
    if (!kept.includes(0) && values.includes(0)) {
        var collides = kept.find(function (k) { return Math.abs(yOf(k) - yOf(0)) < minGapPx; });
        if (collides !== undefined)
            kept.splice(kept.indexOf(collides), 1);
        kept.push(0);
    }
    return kept;
}
function TooltipBox(_a) {
    var tip = _a.tip;
    // Flip to the other side past the midpoint so the tooltip never runs off the plot.
    var flip = tip.xPct > 55;
    return ((0, jsx_runtime_1.jsxs)("div", { className: "pointer-events-none absolute z-20 min-w-[210px] rounded-lg border border-default-200 bg-content1 px-3 py-2 shadow-lg", style: {
            left: flip ? undefined : "calc(".concat(tip.xPct, "% + 12px)"),
            right: flip ? "calc(".concat(100 - tip.xPct, "% + 12px)") : undefined,
            top: tip.y,
        }, children: [(0, jsx_runtime_1.jsx)("p", { className: "mb-1 text-xs font-semibold text-foreground", children: tip.title }), tip.rows.map(function (r) { return ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center justify-between gap-4 py-0.5 text-xs", children: [(0, jsx_runtime_1.jsxs)("span", { className: "flex items-center gap-1.5 text-default-500", children: [r.color && (0, jsx_runtime_1.jsx)("span", { className: "inline-block h-2 w-2 shrink-0 rounded-sm", style: { background: r.color } }), r.label] }), (0, jsx_runtime_1.jsx)("span", { className: "font-medium tabular-nums text-foreground", children: r.value })] }, r.label)); })] }));
}
/**
 * Cost composition over time — a stacked area chart.
 *
 * Each band is a filled polygon between its own cumulative top and the cumulative top of everything
 * below it, so the upper edge of the stack is the period total. A 2px stroke in the surface colour
 * separates neighbouring bands, which is what keeps eight hues legible where they touch.
 *
 * Reading an area stack fairly: only the bottom band sits on a flat baseline, so the bands above are
 * hard to compare against each other by eye. The tooltip gives every band's exact value at the
 * hovered period, and the table view carries the numbers — the shape is for composition and trend,
 * not for reading one series off the page.
 */
function StackedCostChart(_a) {
    var _b;
    var periods = _a.periods, series = _a.series, isDark = _a.isDark;
    var _c = (0, react_1.useState)(null), hover = _c[0], setHover = _c[1];
    var palette = isDark ? exports.SERIES_DARK : exports.SERIES_LIGHT;
    var surface = isDark ? "#18181b" : "#ffffff";
    var W = 900;
    var H = 340;
    // Wide enough on the left for a compact money label without clipping.
    var M = { top: 16, right: 16, bottom: 46, left: 62 };
    var plotW = W - M.left - M.right;
    var plotH = H - M.top - M.bottom;
    var totals = periods.map(function (_, i) { return series.reduce(function (sum, s) { var _a; return sum + ((_a = s.values[i]) !== null && _a !== void 0 ? _a : 0); }, 0); });
    var yMax = niceCeil(Math.max.apply(Math, __spreadArray(__spreadArray([], totals, false), [1], false)));
    // Points sit ON the edges so the wave spans the full plot, as a time series should.
    var xAt = function (i) { return (periods.length <= 1 ? M.left + plotW / 2 : M.left + (plotW * i) / (periods.length - 1)); };
    var yAt = function (v) { return M.top + plotH - (v / yMax) * plotH; };
    // Cumulative tops, one row per series.
    var cumulative = [];
    var running = new Array(periods.length).fill(0);
    for (var _i = 0, series_1 = series; _i < series_1.length; _i++) {
        var s = series_1[_i];
        for (var i = 0; i < periods.length; i++)
            running[i] += (_b = s.values[i]) !== null && _b !== void 0 ? _b : 0;
        cumulative.push(__spreadArray([], running, true));
    }
    var ticks = spacedTicks([0, 0.25, 0.5, 0.75, 1].map(function (f) { return f * yMax; }), yAt);
    var hoverTotal = hover !== null ? totals[hover] || 1 : 1;
    return ((0, jsx_runtime_1.jsxs)("div", { className: "relative", children: [(0, jsx_runtime_1.jsxs)("svg", { viewBox: "0 0 ".concat(W, " ").concat(H), className: "w-full", role: "img", "aria-label": "Supplier cost over time by charge", children: [ticks.map(function (t) {
                        var y = yAt(t);
                        return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: M.left, x2: W - M.right, y1: y, y2: y, stroke: t === 0 ? AXIS : GRID, strokeWidth: 1 }), (0, jsx_runtime_1.jsx)("text", { x: M.left - 10, y: y + 4, textAnchor: "end", fontSize: "11", fill: INK_MUTED, className: "tabular-nums", children: moneyAxis(t) })] }, t));
                    }), series.map(function (s, si) {
                        var top = cumulative[si];
                        var bottom = si === 0 ? new Array(periods.length).fill(0) : cumulative[si - 1];
                        var topEdge = top.map(function (v, i) { return "".concat(xAt(i), ",").concat(yAt(v)); }).join(" L ");
                        var bottomEdge = bottom
                            .map(function (v, i) { return "".concat(xAt(i), ",").concat(yAt(v)); })
                            .reverse()
                            .join(" L ");
                        return ((0, jsx_runtime_1.jsx)("path", { d: "M ".concat(topEdge, " L ").concat(bottomEdge, " Z"), fill: palette[si % palette.length], stroke: surface, strokeWidth: 2, strokeLinejoin: "round", opacity: hover === null ? 1 : 0.92 }, s.name));
                    }), hover !== null && ((0, jsx_runtime_1.jsx)("line", { x1: xAt(hover), x2: xAt(hover), y1: M.top, y2: M.top + plotH, stroke: AXIS, strokeWidth: 1, strokeDasharray: "3 3" })), periods.map(function (period, i) {
                        var bandW = plotW / Math.max(periods.length, 1);
                        return ((0, jsx_runtime_1.jsx)("rect", { x: xAt(i) - bandW / 2, y: M.top, width: bandW, height: plotH, fill: "transparent", onMouseEnter: function () { return setHover(i); }, onMouseLeave: function () { return setHover(null); } }, period));
                    }), periods.map(function (period, i) { return ((0, jsx_runtime_1.jsx)("text", { x: xAt(i), y: H - M.bottom + 18, textAnchor: "middle", fontSize: "11", fill: INK_MUTED, children: (0, exports.periodLabel)(period) }, period)); })] }), hover !== null && ((0, jsx_runtime_1.jsx)(TooltipBox, { tip: {
                    xPct: (xAt(hover) / W) * 100,
                    y: 8,
                    title: (0, exports.periodLabel)(periods[hover]),
                    rows: __spreadArray([
                        { label: "Total", value: moneyExact(totals[hover]) }
                    ], series
                        .map(function (s, si) { var _a; return ({ s: s, si: si, v: (_a = s.values[hover]) !== null && _a !== void 0 ? _a : 0 }); })
                        .filter(function (_a) {
                        var v = _a.v;
                        return v > 0;
                    })
                        .sort(function (a, b) { return b.v - a.v; })
                        .map(function (_a) {
                        var s = _a.s, si = _a.si, v = _a.v;
                        return ({
                            label: "".concat(s.name, "  ").concat(((v / hoverTotal) * 100).toFixed(0), "%"),
                            value: moneyExact(v),
                            color: palette[si % palette.length],
                        });
                    }), true),
                } })), (0, jsx_runtime_1.jsx)("div", { className: "mt-3 flex flex-wrap gap-x-4 gap-y-1.5", children: series.map(function (s, i) { return ((0, jsx_runtime_1.jsxs)("span", { className: "flex items-center gap-1.5 text-xs text-default-600", children: [(0, jsx_runtime_1.jsx)("span", { className: "inline-block h-2.5 w-2.5 rounded-sm", style: { background: palette[i % palette.length] } }), s.name] }, s.name)); }) })] }));
}
/**
 * Recovery profit per charge — the same wave as the cost chart, but signed.
 *
 * Profit crosses zero, so bands cannot simply stack upwards. Positive contributions stack away from
 * the zero line going up and negative ones going down, each from its own running baseline — which is
 * what Excel does, and the only way a stack stays readable with mixed signs. A series that is
 * positive one month and negative the next has a band that crosses the axis, and that is the honest
 * picture rather than an artefact.
 *
 * The zero line is drawn heavier than the other gridlines because with a signed stack it is the
 * reference the whole chart hangs off.
 */
function StackedProfitChart(_a) {
    var _b, _c;
    var periods = _a.periods, series = _a.series, totals = _a.totals, isDark = _a.isDark;
    var _d = (0, react_1.useState)(null), hover = _d[0], setHover = _d[1];
    var palette = isDark ? exports.SERIES_DARK : exports.SERIES_LIGHT;
    var surface = isDark ? "#18181b" : "#ffffff";
    var W = 900;
    var H = 320;
    var M = { top: 16, right: 16, bottom: 46, left: 62 };
    var plotW = W - M.left - M.right;
    var plotH = H - M.top - M.bottom;
    // Per series per period: the band runs from `base` to `top`, both signed.
    var bands = series.map(function () { return ({ base: [], top: [] }); });
    var axisMax = 0;
    var axisMin = 0;
    for (var i = 0; i < periods.length; i++) {
        var posRunning = 0;
        var negRunning = 0;
        for (var si = 0; si < series.length; si++) {
            var v = (_b = series[si].values[i]) !== null && _b !== void 0 ? _b : 0;
            if (v >= 0) {
                bands[si].base[i] = posRunning;
                bands[si].top[i] = posRunning + v;
                posRunning += v;
            }
            else {
                bands[si].top[i] = negRunning;
                bands[si].base[i] = negRunning + v;
                negRunning += v;
            }
        }
        axisMax = Math.max(axisMax, posRunning);
        axisMin = Math.min(axisMin, negRunning);
    }
    var top = niceCeil(Math.max(axisMax, 1));
    var bottom = axisMin < 0 ? -niceCeil(Math.abs(axisMin)) : 0;
    var span = top - bottom || 1;
    var xAt = function (i) { return (periods.length <= 1 ? M.left + plotW / 2 : M.left + (plotW * i) / (periods.length - 1)); };
    var yAt = function (v) { return M.top + plotH - ((v - bottom) / span) * plotH; };
    var ticks = spacedTicks([top, top / 2, 0, bottom / 2, bottom], yAt);
    var bandW = plotW / Math.max(periods.length, 1);
    return ((0, jsx_runtime_1.jsxs)("div", { className: "relative", children: [(0, jsx_runtime_1.jsxs)("svg", { viewBox: "0 0 ".concat(W, " ").concat(H), className: "w-full", role: "img", "aria-label": "Recovery profit over time by charge", children: [ticks.map(function (t) { return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: M.left, x2: W - M.right, y1: yAt(t), y2: yAt(t), stroke: t === 0 ? AXIS : GRID, strokeWidth: t === 0 ? 1.5 : 1 }), (0, jsx_runtime_1.jsx)("text", { x: M.left - 10, y: yAt(t) + 4, textAnchor: "end", fontSize: "11", fill: INK_MUTED, className: "tabular-nums", children: moneyAxis(t) })] }, t)); }), series.map(function (s, si) {
                        var topEdge = bands[si].top.map(function (v, i) { return "".concat(xAt(i), ",").concat(yAt(v)); }).join(" L ");
                        var baseEdge = bands[si].base
                            .map(function (v, i) { return "".concat(xAt(i), ",").concat(yAt(v)); })
                            .reverse()
                            .join(" L ");
                        return ((0, jsx_runtime_1.jsx)("path", { d: "M ".concat(topEdge, " L ").concat(baseEdge, " Z"), fill: palette[si % palette.length], stroke: surface, strokeWidth: 2, strokeLinejoin: "round" }, s.name));
                    }), hover !== null && ((0, jsx_runtime_1.jsx)("line", { x1: xAt(hover), x2: xAt(hover), y1: M.top, y2: M.top + plotH, stroke: AXIS, strokeWidth: 1, strokeDasharray: "3 3" })), periods.map(function (period, i) { return ((0, jsx_runtime_1.jsx)("rect", { x: xAt(i) - bandW / 2, y: M.top, width: bandW, height: plotH, fill: "transparent", onMouseEnter: function () { return setHover(i); }, onMouseLeave: function () { return setHover(null); } }, period)); }), periods.map(function (period, i) { return ((0, jsx_runtime_1.jsx)("text", { x: xAt(i), y: H - M.bottom + 18, textAnchor: "middle", fontSize: "11", fill: INK_MUTED, children: (0, exports.periodLabel)(period) }, "".concat(period, "-label"))); })] }), hover !== null && ((0, jsx_runtime_1.jsx)(TooltipBox, { tip: {
                    xPct: (xAt(hover) / W) * 100,
                    y: 8,
                    title: (0, exports.periodLabel)(periods[hover]),
                    rows: __spreadArray([
                        { label: "Total profit", value: moneyExact((_c = totals[hover]) !== null && _c !== void 0 ? _c : 0) }
                    ], series
                        .map(function (s, si) { var _a; return ({ s: s, si: si, v: (_a = s.values[hover]) !== null && _a !== void 0 ? _a : 0 }); })
                        .filter(function (_a) {
                        var v = _a.v;
                        return v !== 0;
                    })
                        .sort(function (a, b) { return Math.abs(b.v) - Math.abs(a.v); })
                        .map(function (_a) {
                        var s = _a.s, si = _a.si, v = _a.v;
                        return ({ label: s.name, value: moneyExact(v), color: palette[si % palette.length] });
                    }), true),
                } })), (0, jsx_runtime_1.jsx)("div", { className: "mt-3 flex flex-wrap gap-x-4 gap-y-1.5", children: series.map(function (s, i) { return ((0, jsx_runtime_1.jsxs)("span", { className: "flex items-center gap-1.5 text-xs text-default-600", children: [(0, jsx_runtime_1.jsx)("span", { className: "inline-block h-2.5 w-2.5 rounded-sm", style: { background: palette[i % palette.length] } }), s.name] }, s.name)); }) }), (0, jsx_runtime_1.jsx)("p", { className: "mt-2 text-xs text-default-400", children: "Bands above the line recovered more than they cost; bands below cost more than they recovered." })] }));
}
